import { LlmModel, type LlmModelSelectDto } from '@@types/api.types';
import { TooltipComponent } from '@components/core/tooltip.component';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { cn } from '@lib/utils';
import { useLlmModelsQueryOptions } from '@queries/llm.query';
import { llmModelStore } from '@stores/llm-model.store';
import { useQuery } from '@tanstack/react-query';
import { Braces, Search, Wrench } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useStore } from 'zustand';

export type LlmModelSelectorProps = {
  className?: string;
  modelFilter?: (model: LlmModelSelectDto) => boolean;
  showCapabilityIcons?: boolean;
};

type ModelCapabilityIconsProps = {
  model: LlmModelSelectDto;
};

const ModelCapabilityIcons: React.FC<ModelCapabilityIconsProps> = (props) => {
  const { model } = props;

  return (
    <span className="inline-flex shrink-0 items-center gap-1.5">
      {model.hasTools && (
        <TooltipComponent
          side="top"
          trigger={<Wrench className="size-4 text-blue-500" />}
        >
          Supports tool/function calling
        </TooltipComponent>
      )}
      {model.hasWebSearch && (
        <TooltipComponent
          side="top"
          trigger={<Search className="size-4 text-green-500" />}
        >
          Can search the web
        </TooltipComponent>
      )}
      {/* UPDO: These capabilities are coming soon */}
      {/* {model.hasMultimodal && (
        <TooltipComponent
          side="top"
          trigger={<Image className="size-4 text-purple-500" />}
        >
          Multimodal support (Coming soon)
        </TooltipComponent>
      )}
      {model.hasReasoning && (
        <TooltipComponent
          side="top"
          trigger={<BrainCircuit className="size-4 text-pink-500" />}
        >
          Advanced reasoning (Coming soon)
        </TooltipComponent>
      )} */}
      {model.hasStructuredOutputs && (
        <TooltipComponent
          side="top"
          trigger={<Braces className="size-4 text-orange-500" />}
        >
          Supports structured JSON outputs
        </TooltipComponent>
      )}
    </span>
  );
};

export const LlmModelSelector: React.FC<LlmModelSelectorProps> = (props) => {
  const { className, modelFilter, showCapabilityIcons = false } = props;

  const aiModelsQueryOptions = useLlmModelsQueryOptions();
  const { data: allModels = [], isLoading, isSuccess } = useQuery(aiModelsQueryOptions);
  const { selectedModel, setSelectedModel } = useStore(llmModelStore);
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  const models = useMemo(() => {
    let filteredModels = allModels.filter((model) => !model.archived);

    if (modelFilter) {
      filteredModels = filteredModels.filter(modelFilter);
    }

    return filteredModels;
  }, [allModels, modelFilter]);

  const firstAvailableModel = useMemo(() => {
    return models.length > 0 ? models[0].value : null;
  }, [models]);

  const handleValueChange = (value: string) => {
    const nextModel = Object.values(LlmModel).find((m) => m === value);
    if (!nextModel) {
      console.warn(`Invalid AI model value received: ${value}`);
      return;
    }

    setSelectedModel(nextModel);
  };

  const normalizedValue = useMemo(() => {
    if (!selectedModel) return firstAvailableModel;
    if (isLoading || !isSuccess) return selectedModel;

    const modelExists = models?.some((m) => m.value === selectedModel);
    return modelExists ? selectedModel : firstAvailableModel;
  }, [selectedModel, models, isLoading, isSuccess, firstAvailableModel]);

  // Keep the store value in sync with the UI. If there's no selected model yet, or
  // the current selection becomes unavailable/filtered out, fall back to the
  // first available model and update the store so that the rest of the app
  // (e.g. API calls) stays consistent with what the user sees in the UI.
  useEffect(() => {
    if (isLoading || !isSuccess) return;

    if (normalizedValue && normalizedValue !== selectedModel) {
      setSelectedModel(normalizedValue);
    }
  }, [normalizedValue, selectedModel, setSelectedModel, isLoading, isSuccess]);

  const normalizedLabel = useMemo(() => {
    const model = models?.find((m) => m.value === normalizedValue);
    return model?.label || '';
  }, [normalizedValue, models]);

  const selectComponent = (
    <Select
      onValueChange={handleValueChange}
      value={normalizedValue || ''}
      open={isSelectOpen}
      onOpenChange={setIsSelectOpen}
    >
      <SelectTrigger
        className={cn('h-7 w-auto min-w-16 text-xs border-gray-200 bg-gray-50 hover:bg-gray-100', className)}
      >
        <SelectValue placeholder="Select model">
          <span className="text-xs">{normalizedLabel}</span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="min-w-[280px]">
        {models?.map((model) => (
          <SelectItem
            key={model.value}
            value={model.value}
          >
            <div className="flex flex-1 w-full min-w-0 items-center gap-2">
              <span className="flex-1 min-w-0 truncate text-xs">{model.label}</span>
              {showCapabilityIcons && <ModelCapabilityIcons model={model} />}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  // Show tooltip when "Auto" model is selected (first model in the list)
  const isAutoModel = normalizedValue === LlmModel.OpenrouterAuto;

  if (isAutoModel && !isSelectOpen) {
    return (
      <TooltipComponent
        trigger={<div className="inline-block">{selectComponent}</div>}
        side="top"
      >
        OpenRouter will choose the best model for you.
      </TooltipComponent>
    );
  }

  return selectComponent;
};
